/* UTILS */
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { STATIC_DATA_PATHS } from '@/enums/constants';
import type { VoiceQuestion } from '@/types/enums/enums.types';
import type { JobOrPmAcronymsResponse, Recommendations } from '@/types/jobs/jobs.types';

import { useGlobalStore } from './global';

type InitialState = {
  jobsAcronyms: JobOrPmAcronymsResponse;
  voiceQuestions: VoiceQuestion[];
  skillRecommendations: Recommendations;
  skillLevels: { [key: string]: { body: string; score_min: number; title: string } };
};

export const useEnumsStore = defineStore('enumsStore', () => {
  const staticDataUrl = useGlobalStore().getStaticDataUrl;
  const initialState: InitialState = {
    jobsAcronyms: {},
    skillRecommendations: {},
    voiceQuestions: [],
    skillLevels: {}
  };

  // VARIABLES
  const jobsAcronyms = ref(initialState.jobsAcronyms);
  const skillRecommendations = ref(initialState.skillRecommendations);
  const voiceQuestions = ref(initialState.voiceQuestions);
  const skillLevels = ref(initialState.skillLevels);

  // GETTERS
  const getJobsAcronyms = computed(() => jobsAcronyms.value);
  const getSkillRecommendations = computed(() => skillRecommendations.value);
  const getVoiceQuestions = computed(() => voiceQuestions.value);
  const getSkillLevels = computed(() => skillLevels.value);

  // FUNCTIONS
  async function loadJobsAcronyms() {
    if (!Object.entries(jobsAcronyms.value).length) {
      const response = await fetch(`${staticDataUrl}${STATIC_DATA_PATHS.jobs_acronyms}`);
      jobsAcronyms.value = await response.json();
    }
    return jobsAcronyms.value;
  }

  async function loadVoiceQuestions() {
    if (!voiceQuestions.value.length) {
      const response = await fetch(`${staticDataUrl}${STATIC_DATA_PATHS.voice_questions}`);
      voiceQuestions.value = await response.json();
    }
    return voiceQuestions.value;
  }

  async function loadSkillRecommendations() {
    if (!Object.entries(skillRecommendations.value).length) {
      const response = await fetch(`${staticDataUrl}${STATIC_DATA_PATHS.skill_recommendations}`);
      skillRecommendations.value = await response.json();
    }
    return skillRecommendations.value;
  }

  async function loadSkillLevels() {
    if (!Object.entries(skillLevels.value).length) {
      const response = await fetch(`${staticDataUrl}${STATIC_DATA_PATHS.skill_levels}`);
      skillLevels.value = await response.json();
    }
    return skillLevels.value;
  }

  return {
    getJobsAcronyms,
    getVoiceQuestions,
    getSkillRecommendations,
    getSkillLevels,
    loadJobsAcronyms,
    loadVoiceQuestions,
    loadSkillRecommendations,
    loadSkillLevels
  };
});
